import { FC, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { KTSVG } from '../../../_metronic/helpers';

/**
 * Alt bardaki "Masaüstü Uygulaması" indirme butonu.
 *
 * Sunucuda yayınlanmış kurulum dosyası yoksa (204) buton hiç görünmez.
 * Masaüstü uygulamasının içinde çalışırken de gizlenir — zaten kurulu.
 */

type Platform = {
  file: string;
  label: string;
  arch?: string;
  size?: number;
  url: string;
};

type Installers = {
  version: string;
  publishedAt?: number;
  platforms: Partial<Record<'mac' | 'win', Platform>>;
};

const PLATFORM_META: Record<'mac' | 'win', { icon: string; name: string }> = {
  mac: { icon: '/media/icons/duotone/Devices/Laptop-macbook.svg', name: 'macOS' },
  win: { icon: '/media/icons/duotone/Devices/Display1.svg', name: 'Windows' },
};

const formatSize = (bytes?: number) => (bytes ? `${(bytes / 1024 / 1024).toFixed(0)} MB` : '');

/** Kullanıcının işletim sistemini tahmin eder — önerilen seçeneği öne çıkarmak için. */
const detectPlatform = (): 'mac' | 'win' | null => {
  const ua = `${navigator.userAgent} ${navigator.platform || ''}`.toLowerCase();
  if (ua.includes('mac')) return 'mac';
  if (ua.includes('win')) return 'win';
  return null;
};

const DesktopDownloadButton: FC = () => {
  const [installers, setInstallers] = useState<Installers | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDesktopApp = Boolean((window as any).aycrmDesktop);

  useEffect(() => {
    if (isDesktopApp) return;
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_API_URL}/desktop-update/installers`)
      .then((res) => {
        // Yayın yoksa backend 204 döner (gövde boş)
        if (!cancelled && res.status === 200 && res.data?.platforms) setInstallers(res.data);
      })
      .catch(() => {
        /* indirme kanalı kapalıysa buton görünmez */
      });
    return () => {
      cancelled = true;
    };
  }, [isDesktopApp]);

  // Dışarı tıklayınca / Esc ile kapat
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const download = useCallback((platform: Platform) => {
    const a = document.createElement('a');
    a.href = platform.url;
    a.download = platform.file;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setOpen(false);
  }, []);

  if (isDesktopApp || !installers) return null;

  const suggested = detectPlatform();
  const entries = (Object.keys(installers.platforms) as Array<'mac' | 'win'>)
    .filter((key) => installers.platforms[key])
    .sort((a, b) => (a === suggested ? -1 : b === suggested ? 1 : 0));

  return (
    <div className="position-relative d-inline-flex" ref={containerRef}>
      <button
        type="button"
        className="footer-sticky-icon footer-sticky-icon--primary"
        title="Masaüstü uygulamasını indir"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <KTSVG path="/media/icons/duotone/Files/Cloud-download.svg" className="footer-sticky-icon__svg" />
      </button>

      {open && (
        // Alt bar sticky olduğu için menü yukarı doğru açılır
        <div
          className="bg-white shadow-lg rounded border position-absolute"
          style={{ bottom: '100%', left: 0, marginBottom: '0.5rem', width: 288, zIndex: 1096 }}
        >
          <div className="px-4 py-3 border-bottom">
            <div className="fw-bold text-dark">Masaüstü Uygulaması</div>
            <div className="text-muted fs-8">Sürüm {installers.version}</div>
          </div>

          <div className="p-2">
            {entries.map((key) => {
              const platform = installers.platforms[key] as Platform;
              const meta = PLATFORM_META[key];
              return (
                <button
                  key={key}
                  type="button"
                  className="btn btn-active-light w-100 d-flex align-items-center text-start px-3 py-2"
                  onClick={() => download(platform)}
                >
                  <KTSVG path={meta.icon} className="svg-icon-2 text-gray-600 me-3 flex-shrink-0" />
                  <span className="flex-grow-1" style={{ minWidth: 0 }}>
                    {/* Rozet ad satırında durur; aksi halde alt satırı sıkıştırıp metni kırıyor */}
                    <span className="d-flex align-items-center">
                      <span className="fw-semibold text-dark fs-7">{meta.name}</span>
                      {key === suggested && (
                        <span className="badge badge-light-primary fs-9 ms-2">Önerilen</span>
                      )}
                    </span>
                    <span className="d-block text-muted fs-8 text-truncate">
                      {[platform.arch, formatSize(platform.size)].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="px-4 py-2 border-top text-muted fs-9">
            Kurulumdan sonra güncellemeler otomatik gelir.
          </div>
        </div>
      )}
    </div>
  );
};

export { DesktopDownloadButton };
